import { BehaviorSubject, Observable, of } from 'rxjs';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from '../auth/auth.service';
import { LogoutRequest } from '../../login/models/logoutRequest';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  screenSize: string = '';
  private isBrowser: boolean;

  constructor(
    private auth: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  isKeyValid() {
    return localStorage.getItem('gptKeyValid') === 'true';
  }

  setUserInfo(user: any) {
    localStorage.setItem('userInfo', JSON.stringify(user));
  }

  deleteUserInfo() {
    localStorage.removeItem('userInfo');
  }

  getUserInfo() {
    return localStorage.getItem('userInfo');
  }

  storeGPTKey(gptKey: any) {
    localStorage.setItem('gptKey', gptKey);
    localStorage.setItem('gptKeyValid', 'true');
  }

  getGPTKey() {
    return localStorage.getItem('gptKey');
  }

  deleteGPTKey() {
    localStorage.removeItem('gptKey');
  }

  getUserImage() {
    let userInfo = this.getUserInfo();

    if (userInfo) {
      let userObject = JSON.parse(userInfo);
      if (!userInfo.includes('Guest User')) {
        return userObject.picture;
      }
    }
    return 'assets/default-avatar.jpg';
  }

  setTokenInfo(
    accessToken: string,
    refreshToken: string,
    authUserId: string,
    role: any
  ) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('userId', authUserId);
    localStorage.setItem('roleId', role.roleId);
  }

  setAccessToken(newToken: string) {
    localStorage.setItem('accessToken', newToken);
  }

  deleteTokenInfo() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('role');
  }

  deleteAccessToken() {
    localStorage.removeItem('accessToken');
  }

  getAcessToken() {
    return localStorage.getItem('accessToken');
  }

  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  getUserId() {
    return localStorage.getItem('userId');
  }

  isUserLogin() {
    return !!this.getAcessToken() && !!this.refreshToken();
  }

  async logInAsGuest() {
    let userInfo = this.collectUserInfo();
    this.auth.loginWithGoogle(userInfo).subscribe(
      (response: any) => {
        this.setTokenInfo(
          response.accessToken,
          response.refreshToken,
          response.userId,
          response.role
        );
      },
      () => { }
    );
  }

  async logInWithGoogleAccount(user: any) {
    this.auth.loginWithGoogle(user).subscribe(
      (response: any) => {
        this.setTokenInfo(
          response.accessToken,
          response.refreshToken,
          response.userId,
          response.role
        );
        if (this.isBrowser) {
          window.location.reload();
        }
      },
      () => {
        setTimeout(() => {
          if (this.isBrowser) {
            window.location.reload();
          }
        }, 3000);
      }
    );
  }

  logoutFromGoogleAccount() {
    let logoutParams: any = {};
    if (typeof document !== 'undefined') {
      logoutParams = { returnTo: document.location.origin };
    }

    this.auth.logoutFromGoogle({ logoutParams });

    const refreshToken = this.getRefreshToken();
    localStorage.clear();

    if (refreshToken) {
      const logoutRequest: LogoutRequest = { refreshToken };
      return this.auth.logoutFromGoogle(logoutRequest); // still returns the expected Observable
    }

    return of(null); // ✅ Ensures a value is returned in all paths
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      const logoutRequest: LogoutRequest = { refreshToken };
      return this.auth.refreshAccessToken(logoutRequest);
    } else return new BehaviorSubject(null);
  }

  getScreenSize() {
    return this.screenSize;
  }

  setScreenSize(screenSize: string) {
    this.screenSize = screenSize;
  }

  collectUserInfo() {
    return {
      screenSize: this.getScreenSizeAsString(),
      operatingSystem: this.getOperatingSystem(),
      userSource: this.isBrowser && typeof document !== 'undefined'
        ? document.referrer || 'Direct'
        : 'Direct',
    };
  }

  private getScreenSizeAsString(): string {
    if (this.isBrowser && typeof window !== 'undefined' && typeof document !== 'undefined') {
      const width =
        window.innerWidth ||
        document.documentElement?.clientWidth ||
        document.body?.clientWidth ||
        0;

      const height =
        window.innerHeight ||
        document.documentElement?.clientHeight ||
        document.body?.clientHeight ||
        0;

      return `${width} X ${height}`;
    }

    return '0 X 0';
  }

  private getOperatingSystem(): string {
    if (!this.isBrowser || typeof window === 'undefined') return 'Unknown';

    const userAgent = window.navigator.userAgent;

    switch (true) {
      case /Windows/.test(userAgent):
        return 'Windows';
      case /Mac/.test(userAgent):
        return 'Mac OS';
      case /iPhone|iPad/.test(userAgent):
        return 'iOS';
      case /Android/.test(userAgent):
        return 'Android';
      case /Linux/.test(userAgent):
        return 'Linux';
      case /CrOS/.test(userAgent):
        return 'Chrome OS';
      case /Windows Phone/.test(userAgent):
        return 'Windows Phone';
      default:
        return 'Unknown';
    }
  }
}
